import React, { useEffect, useState } from 'react';
import Modal, { ModalActions, ModalContent, ModalTitle } from '../basic/Modal';
import LacrosseAttackField from './LacrosseAttackField';
import { ShotLocation, ShotOutcome, buildShotLocation } from '../../utils/shotLocation';

const TITLES: Record<ShotOutcome, string> = {
  goal: 'They Scored',
  saved: 'Their Shot — Saved',
  missed: 'Their Shot — Missed',
  blocked: 'Their Shot — Blocked',
};

interface ThemShotLocationSheetProps {
  /** Outcome being recorded; null keeps the sheet closed. */
  outcome: ShotOutcome | null;
  /** Continue recording the shot, with or without a location. */
  onDone: (location: ShotLocation | null) => void;
  /** Abort — nothing is recorded. */
  onCancel: () => void;
}

const ThemShotLocationSheet: React.FC<ThemShotLocationSheetProps> = ({ outcome, onDone, onCancel }) => {
  const [pin, setPin] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setPin(null);
  }, [outcome]);

  if (!outcome) return null;

  const confirm = () => {
    if (!pin) return;
    onDone(buildShotLocation(pin.x, pin.y, 'live'));
  };

  return (
    <Modal isOpen onClose={onCancel} maxWidth="xl" closeOnBackdropClick={false}>
      <ModalTitle onClose={onCancel}>{TITLES[outcome]}</ModalTitle>
      <ModalContent>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col px-1">
            <span className="text-sm font-semibold text-sd-dark">Tap where their shot came from</span>
            <span className="text-[11px] text-gray-500">
              {pin ? 'Move the pin if you need to, then confirm to save.' : 'Place the shot, then confirm to save.'}
            </span>
          </div>
          <LacrosseAttackField
            interactive
            showZoneGuides
            onTap={(x, y) => setPin({ x, y })}
            pin={pin ? { ...pin, outcome } : null}
            className="max-w-[520px] mx-auto"
          />
        </div>
      </ModalContent>
      <ModalActions justify="between">
        <button
          type="button"
          onClick={() => onDone(null)}
          className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 font-semibold"
        >
          Skip location
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={!pin}
          className="px-4 py-2 rounded-md bg-sd-dark text-white font-semibold disabled:opacity-40"
        >
          Confirm
        </button>
      </ModalActions>
    </Modal>
  );
};

export default ThemShotLocationSheet;
